import { useEffect, useRef, useState } from 'preact/hooks';
import type { BuiltinAnswers, PublicEvent } from '../../shared/types';
import { renderPdf, type SignatureInput } from '../lib/pdf';
import { useStandardPdf } from './pdfPreview';
import { SignaturePad } from '../components/SignaturePad';
import { participantName } from '../../shared/builtin';
import { reportClientError } from '../lib/clientErrors';

const PERMISSION_TEXT = [
  'I give permission for my child or youth (or if signing on my own behalf, as a leader attending this activity, I personally consent) to participate in the event and activities listed above (unless noted) and authorize the adult leaders supervising this event to administer emergency treatment to the above-named participant for any accident or illness and to act in my stead in approving necessary medical care. This authorization shall cover this event and travel to and from this event.',
  'Please note: Units may not have the ability to meet all medical, physical, and other accommodations and are asked to counsel with parents or guardians on what is possible.',
  'The participant is responsible for his or her own conduct and is aware of and agrees to abide by Church standards, camp or event safety rules, and other pertinent instructions. The participant’s conduct and interactions should abide by Church standards and exemplify Christlike behavior, including those listed on the attached “Conduct at Church Activities.”',
  'Parents and participants should understand that participation in an activity is not a right but a privilege that can be revoked if participants behave inappropriately or if they pose a risk to themselves or others.',
  'This information is collected to help event and activity leaders or medical personnel so they can be prepared and appropriately respond to health concerns or an emergency. It will be kept confidential and shared only as needed.',
];

/** A PDF's pages drawn on the page, with a link to open it full size unless `fullSizeLink` is false. */
export function PdfPreview({ bytes, pages, fullSizeLink = true }: { bytes: Uint8Array | null; pages?: number[]; fullSizeLink?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!bytes) return;
    const u = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [bytes]);
  useEffect(() => {
    if (!bytes || !ref.current) return;
    let cancelled = false;
    setErr(null);
    ref.current.innerHTML = '';
    renderPdf(bytes, 1.4).then((canvases) => {
      if (cancelled || !ref.current) return;
      canvases.forEach((c, i) => { if (!pages || pages.includes(i)) { c.className = 'pdf-page'; ref.current!.appendChild(c); } });
    }).catch((error) => { reportClientError('register: show permission form', error); if (!cancelled) setErr(fullSizeLink ? 'Please open the form full size using the link above.' : 'Please try again in a moment.'); });
    return () => { cancelled = true; };
  }, [bytes, pages?.join(',')]);
  if (!bytes) return <p class="muted">Preparing the form…</p>;
  return (
    <>
      {fullSizeLink && url && <p class="small"><a href={url} target="_blank" rel="noopener">Open full size in a new tab</a></p>}
      {err && <p class="alert">Could not render preview. {err}</p>}<div class="pdf-preview" ref={ref} />
    </>
  );
}

interface Props {
  ev: PublicEvent; b: BuiltinAnswers; template: Uint8Array | null; signedOn: string;
  participantSig: SignatureInput | null; parentSig: SignatureInput | null;
  onParticipantSig: (s: SignatureInput | null) => void; onParentSig: (s: SignatureInput | null) => void;
  error?: string | null; participantError?: string; parentError?: string;
  /** The participant is younger than the event's age for signing alone on the day of signing, so a parent or guardian
   * must also sign. An adult signs alone. */
  minor: boolean;
}

export function PermissionStep(p: Props) {
  const standard = useStandardPdf('standard-preview', p.template ? { template: p.template, event: p.ev, answers: p.b, signedOn: p.signedOn, flatten: true } : null);
  const preview = standard.artifact?.bytes ?? null;

  return (
    <div class="stack">
      <h2 tabIndex={-1}>Permission and Medical Release Form</h2>
      {/* The completed permission form, shown in full on the page, not folded away, then what signing it means. */}
      <PdfPreview bytes={preview} pages={[0]} fullSizeLink={false} />
      <div class="permission-text">{PERMISSION_TEXT.map((t) => <p>{t}</p>)}</div>
      <h3>Sign the Permission and Medical Release Form</h3>
      <section class="signer-card"><p class="eyebrow">Participant <span class="required-label">Required</span></p>
      <SignaturePad label="Participant’s signature" signer={participantName(p.b)} error={p.participantError} value={p.participantSig} onChange={p.onParticipantSig} typedDefault={participantName(p.b)} /></section>
      {/* A parent or guardian signs for a minor only; an adult's form has no parent signature. */}
      {p.minor && <section class="signer-card"><p class="eyebrow">Parent or guardian <span class="required-label">Required</span></p>
        <SignaturePad label="Parent or guardian’s signature" signer={p.b.emergencyContact} error={p.parentError} value={p.parentSig} onChange={p.onParentSig} /></section>}
      {p.error && <p class="alert">{p.error}</p>}
    </div>
  );
}
